import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { useChannelHealthStore } from "../library/channelHealthStore.js";
import { usePressAndHold } from "./pressAndHold.js";

export const CHANNEL_ROW_HEIGHT = 56;

interface ChannelRowProps {
  channel: Channel;
  nowPlaying: Program | null;
  isSelected: boolean;
  hasEpgData: boolean;
  onClick: () => void;
  /** Long-press / hold-OK / right-click: the same quick-action menu a programme
   *  opens, for this channel (owner, 2026-09-24). */
  onContextAction?: (point: { x: number; y: number }) => void;
  /** Index of this row in the guide — enables D-pad roving focus. */
  guideRow?: number;
  /** True when this row is the current D-pad target (roving tabindex + ring). */
  isFocused?: boolean;
  /** The row focus is on, on the name or any of its programmes: a cut-off name scrolls. */
  inFocusedRow?: boolean;
}

/** Pixels a second a long name scrolls at: slow enough to read on a TV. */
const NAME_SCROLL_PX_PER_S = 35;

/**
 * How far the row's name must move to show its end, measured when the row
 * takes focus (onn box, 2026-10-23, owner: "can we scroll channel names instead
 * of cutting them off?"). Only the focused row is measured and moves: ten long
 * names scrolling together cannot be read, and each costs frame time on the
 * 1 GB projector. Null when the name fits.
 */
function useNameScroll(on: boolean, name: string) {
  const boxRef = useRef<HTMLSpanElement>(null);
  const [shift, setShift] = useState<number | null>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!on || !box) {
      setShift(null);
      return;
    }
    // The box's own scroll width: the text inside is inline, and an inline
    // element has none (onn box, 2026-10-23: every name measured 0).
    const hidden = box.scrollWidth - box.clientWidth;
    setShift(hidden > 2 ? Math.ceil(hidden) : null);
  }, [on, name]);
  return { boxRef, shift };
}

/** A single channel row in the left sidebar. */
export function ChannelRow({ channel, nowPlaying, isSelected, hasEpgData, onClick, onContextAction, guideRow, isFocused = false, inFocusedRow = isFocused }: ChannelRowProps) {
  // Tap / OK tunes, hold opens the menu — the same gesture as a programme cell.
  const press = usePressAndHold(onClick, onContextAction);
  // Section 12 health badge: the store persists last probe results on-device;
  // only channels that have been checked render a dot. Subscribing per-row
  // keeps the virtualized list from re-rendering wholesale when health updates.
  const health = useChannelHealthStore((s) => s.health[channel.id]);
  const name = useNameScroll(inFocusedRow, channel.name);
  const signal = health?.state ?? "unknown";
  return (
    <div
      className={`epg-channel-row${isSelected ? " epg-channel-row--selected" : ""}${!hasEpgData ? " epg-channel-row--no-data" : ""}${isFocused ? " epg-channel-row--focused" : ""}`}
      style={{ height: CHANNEL_ROW_HEIGHT }}
      data-guide-row={guideRow}
      {...press}
      role="button"
      tabIndex={isFocused ? 0 : -1}
      aria-label={`${channel.number ? channel.number + " " : ""}${channel.name}${nowPlaying ? `: ${nowPlaying.title}` : ""}`}
    >
      {channel.logoUrl ? (
        <img className="epg-channel-logo" src={channel.logoUrl} alt="" loading="lazy" />
      ) : channel.number !== undefined ? (
        <span className="epg-channel-number">{channel.number}</span>
      ) : null}
      <span
        ref={name.boxRef}
        className={`epg-channel-name${name.shift !== null ? " epg-channel-name--scroll" : ""}`}
        title={channel.name}
        style={
          name.shift !== null
            ? ({
                "--name-shift": `-${name.shift}px`,
                // Travel takes 70% of a cycle; the rest is the pause at each end.
                "--name-cycle": `${Math.max(2, name.shift / NAME_SCROLL_PX_PER_S) / 0.7}s`,
              } as CSSProperties)
            : undefined
        }
      >
        <span className="epg-channel-name__text">
          {channel.name}
        </span>
      </span>
      {signal !== "unknown" && health && (
        <span
          className={`epg-health-dot epg-health-dot--${signal}`}
          title={`Signal: ${signal}${health.lastStatus !== null ? ` (HTTP ${health.lastStatus})` : ""} · checked ${formatHealthTime(health.lastCheckedAt)}`}
          aria-label={`${channel.name} signal ${signal}`}
        />
      )}
      {/* No "now playing" line under the name: the programme grid right beside
          it already shows what is on, so the column said it twice (owner,
          2026-10-05). The title stays in the row's spoken label above. */}
    </div>
  );
}

function formatHealthTime(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m} ${ampm}`;
}
